import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Button, Container, Divider, Link, Stack, Typography } from '@mui/material';
import Logo from '../ui/Logo';
import { useAuthStore } from '../../store/authStore';

/** Signed in already: the button opens the app instead of the sign-in form. */
export const useSignInLink = (): { to: string; label: string } => {
  const signedIn = useAuthStore((s) => s.isAuthenticated);
  return signedIn ? { to: '/dashboard', label: 'Open dashboard' } : { to: '/login', label: 'Sign in' };
};

// The chrome for every page you can reach without signing in.
//
// There were three different versions of it: the landing page had a nav and
// a footer, LegalPage had a nav with three links and a "back to the site"
// line, and Support had a fourth nav that listed only two of them and did
// not link to itself. A visitor who followed Privacy from the store listing
// landed somewhere that looked like a different company and offered no way
// back to the product.
//
// One header, one footer, the same links in the same order, everywhere.

const NAV = [
  { label: 'What it does', to: '/#product' },
  { label: 'Download', to: '/#download' },
  { label: 'About', to: '/about' },
  { label: 'Support', to: '/support' },
  { label: 'Privacy', to: '/privacy' },
  { label: 'Terms', to: '/terms' },
];

export const PublicHeader: React.FC = () => {
  const { pathname } = useLocation();
  const signIn = useSignInLink();

  return (
    <Box
      component="nav"
      sx={{
        position: 'sticky', top: 0, zIndex: 10,
        bgcolor: 'background.paper',
        borderBottom: '1px solid', borderColor: 'divider',
      }}
    >
      <Container maxWidth="lg">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1.75 }}>
          <Link component={RouterLink} to="/" sx={{ display: 'flex' }} aria-label="TopoSDS home">
            <Logo height={30} />
          </Link>

          <Stack direction="row" alignItems="center" gap={{ xs: 2, md: 3.5 }}>
            <Stack direction="row" gap={{ xs: 2, md: 3 }} sx={{ display: { xs: 'none', md: 'flex' } }}>
              {NAV.map((link) => {
                const active = link.to === pathname;
                return (
                  <Link
                    key={link.to}
                    component={RouterLink}
                    to={link.to}
                    underline="hover"
                    variant="body2"
                    sx={{
                      fontWeight: active ? 700 : 500,
                      color: active ? 'text.primary' : 'text.secondary',
                      '&:hover': { color: 'text.primary' },
                    }}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </Stack>
            <Button component={RouterLink} to={signIn.to} variant="contained">{signIn.label}</Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export const PublicFooter: React.FC = () => {
  const signIn = useSignInLink();
  return (
  <Box component="footer" sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={2.5}
      >
        <Stack gap={1}>
          <Link component={RouterLink} to="/" sx={{ display: 'flex' }} aria-label="TopoSDS home">
            <Logo height={26} />
          </Link>
          <Typography variant="caption" color="text.secondary">
            The operations engine for delivery contractors.
          </Typography>
        </Stack>

        <Stack direction="row" gap={3} flexWrap="wrap">
          {NAV.map((link) => (
            <Link
              key={link.to}
              component={RouterLink}
              to={link.to}
              underline="hover"
              color="text.secondary"
              variant="body2"
            >
              {link.label}
            </Link>
          ))}
          <Link component={RouterLink} to={signIn.to} underline="hover" color="text.secondary" variant="body2">
            {signIn.label}
          </Link>
        </Stack>
      </Stack>

      <Divider sx={{ my: 3 }} />
      <Typography variant="caption" color="text.disabled">
        © {new Date().getFullYear()} TopoSDS
      </Typography>
    </Container>
  </Box>
  );
};

/** Header, page, footer — for the reading pages. The landing lays out its
 *  own full-width bands between the two. */
const PublicLayout: React.FC<{
  children: React.ReactNode;
  maxWidth?: 'md' | 'lg';
}> = ({ children, maxWidth = 'md' }) => (
  <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
    <PublicHeader />
    <Container maxWidth={maxWidth} sx={{ flex: 1, pt: 6, pb: 10 }}>
      {children}
    </Container>
    <PublicFooter />
  </Box>
);

export default PublicLayout;
